import * as generated from './generated/card-validators.js';
import type { StandaloneError, StandaloneValidate } from './generated/card-validators.js';
import type { Card, CardFile, ContentFile, UseCasesFile } from './types';

/**
 * card-schema.md §4 段階 1 の schema 検証。
 * 本番 CSP では実行時に Ajv で compile できない（new Function）ため、
 * scripts/generate-validators.ts がビルド前に生成した検証関数（Ajv standalone）を使う。
 */

export interface SchemaIssue {
  instancePath: string;
  message: string;
}

export type SchemaResult<T> = { ok: true; value: T } | { ok: false; errors: SchemaIssue[] };

function toIssues(errors: StandaloneError[] | null | undefined): SchemaIssue[] {
  const list = (errors ?? []).map((e) => ({
    instancePath: e.instancePath || '/',
    message: e.message ?? 'invalid',
  }));
  return list.length > 0 ? list : [{ instancePath: '/', message: 'invalid' }];
}

/** 検証関数を型ガードにする。生成物は schema と同じ形を検査するので、型は types.ts の手書きの型を当てる。 */
function guard<T>(v: StandaloneValidate): (data: unknown) => data is T {
  return (data: unknown): data is T => v(data);
}

function run<T>(v: StandaloneValidate, data: unknown): SchemaResult<T> {
  if (guard<T>(v)(data)) return { ok: true, value: data };
  return { ok: false, errors: toIssues(v.errors) };
}

/**
 * content/ の YAML 1 ファイル分を検証する（schema のルート oneOf）。
 * エラーを読みやすくするため、kind があれば use-cases、cards があればカードファイルとして検証し直す。
 */
export function validateContentFile(data: unknown): SchemaResult<ContentFile> {
  if (guard<ContentFile>(generated.validateContent)(data)) return { ok: true, value: data };
  if (typeof data === 'object' && data !== null) {
    if ('kind' in data) return run(generated.validateUseCasesFile, data);
    if ('cards' in data) return run(generated.validateCardFile, data);
  }
  return { ok: false, errors: toIssues(generated.validateContent.errors) };
}

export const validateCardFile = (data: unknown): SchemaResult<CardFile> =>
  run(generated.validateCardFile, data);
export const validateUseCasesFile = (data: unknown): SchemaResult<UseCasesFile> =>
  run(generated.validateUseCasesFile, data);
export const validateCard = (data: unknown): SchemaResult<Card> =>
  run(generated.validateCard, data);

/** 型ガード（schema で検証する）。 */
export const isCard = guard<Card>(generated.validateCard);
export const isCardFile = guard<CardFile>(generated.validateCardFile);
export const isUseCasesFile = guard<UseCasesFile>(generated.validateUseCasesFile);
